import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const NativeCapsContext = createContext({
  isNativeApp: false,
  caps: null,
  kokoroReady: false,
  kokoroWarm: false,
  kokoroError: null,
  kokoroPlaybackActive: false,
  requestCaps: () => { },
  postToNative: () => false,
});

const NATIVE_EVENT_NAME = 'forza:native';

function postToNative(payload) {
  try {
    if (!window?.ReactNativeWebView?.postMessage) {
      return false;
    }
    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export const NativeCapsProvider = ({ children }) => {
  const isNativeApp = Boolean(window?.ReactNativeWebView?.postMessage);

  const [caps, setCaps] = useState(null);
  const [kokoroReady, setKokoroReady] = useState(false);
  const [kokoroWarm, setKokoroWarm] = useState(false);
  const [kokoroError, setKokoroError] = useState(null);
  const [kokoroPlaybackActive, setKokoroPlaybackActive] = useState(false);

  const lastCapsRequestAtRef = useRef(0);

  const requestCaps = useCallback(() => {
    if (!isNativeApp) {
      return;
    }
    lastCapsRequestAtRef.current = Date.now();
    postToNative({ type: 'FORZA_GET_NATIVE_CAPS' });
  }, [isNativeApp]);

  useEffect(() => {
    if (!isNativeApp) {
      return;
    }

    requestCaps();

    const intervalMs = 15_000;
    const interval = setInterval(() => {
      if (!isNativeApp) return;
      if (!caps && Date.now() - lastCapsRequestAtRef.current > intervalMs) {
        requestCaps();
      }
    }, intervalMs);

    return () => clearInterval(interval);
  }, [caps, isNativeApp, requestCaps]);

  useEffect(() => {
    const handler = (event) => {
      const payload = event?.detail;
      if (!payload || typeof payload !== 'object' || typeof payload.type !== 'string') {
        return;
      }

      if (payload.type === 'FORZA_NATIVE_CAPS') {
        setCaps(payload.caps || null);
        return;
      }

      if (payload.type === 'FORZA_KOKORO_READY') {
        setKokoroReady(true);
        setKokoroError(null);
        return;
      }

      if (payload.type === 'FORZA_KOKORO_PREWARM_DONE') {
        setKokoroWarm(true);
        setKokoroError(null);
        return;
      }

      if (payload.type === 'FORZA_KOKORO_ERROR') {
        setKokoroError(payload.error || 'Kokoro error');
        setKokoroReady(false);
        setKokoroWarm(false);
        setKokoroPlaybackActive(false);
        return;
      }

      if (payload.type === 'FORZA_KOKORO_PLAYBACK_STARTED') {
        setKokoroPlaybackActive(true);
        return;
      }

      if (payload.type === 'FORZA_KOKORO_PLAYBACK_ENDED') {
        setKokoroPlaybackActive(false);
        return;
      }

      if (payload.type === 'FORZA_KOKORO_UNLOADED') {
        setKokoroReady(false);
        setKokoroWarm(false);
        setKokoroPlaybackActive(false);
        return;
      }
    };

    window.addEventListener(NATIVE_EVENT_NAME, handler);
    return () => window.removeEventListener(NATIVE_EVENT_NAME, handler);
  }, []);

  const value = useMemo(() => {
    return {
      isNativeApp,
      caps,
      kokoroReady,
      kokoroWarm,
      kokoroError,
      kokoroPlaybackActive,
      requestCaps,
      postToNative,
    };
  }, [caps, isNativeApp, kokoroError, kokoroPlaybackActive, kokoroReady, kokoroWarm, requestCaps]);

  return (
    <NativeCapsContext.Provider value={value}>
      {children}
    </NativeCapsContext.Provider>
  );
};

export const useNativeCaps = () => useContext(NativeCapsContext);
